/**
 * The report's form tabs, and the two ids each one is known by.
 *
 * `route` is the URL segment — what the tab strip and the tab validity map key
 * on. `rulesKey` is the key the form-field-rules payload uses — what the form
 * stores and section components key on. They differ per tab, so neither can be
 * derived from the other; this table is the single place the pairing is stated.
 */
export const REPORT_TABS = [
  { route: 'operational', rulesKey: 'operational' },
  { route: 'cargo-details', rulesKey: 'cargoDetails' },
  { route: 'power', rulesKey: 'power' },
  { route: 'consumptions', rulesKey: 'consumption' },
  { route: 'bunker', rulesKey: 'bunker' },
  { route: 'stock', rulesKey: 'stock' },
  { route: 'position-and-weather', rulesKey: 'positionAndWeather' },
] as const;

export type ReportTabRoute = (typeof REPORT_TABS)[number]['route'];
export type ReportTabRulesKey = (typeof REPORT_TABS)[number]['rulesKey'];

const RULES_KEYS = new Set<string>(REPORT_TABS.map((tab) => tab.rulesKey));

/**
 * Narrows a raw tab id to a form tab. A template keys its tabs by plain string,
 * so it can hold a tab that the form does not have. This tells the two apart.
 */
export const isReportTabRulesKey = (value: string): value is ReportTabRulesKey => RULES_KEYS.has(value);

/** In the tab strip, but has no form state — so no rules key and no validity. */
export const REPORT_HISTORY_ROUTE = 'report-history';

export type ReportTabStripRoute = ReportTabRoute | typeof REPORT_HISTORY_ROUTE;

const TAB_STRIP_ROUTES = new Set<string>([...REPORT_TABS.map((tab) => tab.route), REPORT_HISTORY_ROUTE]);

/** Narrows a raw URL segment to a tab, so the strip can key off the active route. */
export const isReportTabStripRoute = (value: string | null | undefined): value is ReportTabStripRoute =>
  !!value && TAB_STRIP_ROUTES.has(value);
